import { differenceInSeconds } from "date-fns";

import React, { useEffect, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { useReservationContext } from "../../hooks/useReservationExpiry";
import { useLocation, useMatch, useNavigate, useParams } from "react-router-dom";
import { cancelCheckout, repinReservation } from "../../dao/reservation";
import { Spinner } from "@hooplab/ui-components";
import { paths } from "../../App";
import { ReactComponent as IconTimeOutline } from "../../assets/icons/time_outline.svg";
import { ReactComponent as IconArrowRight } from "../../assets/icons/chevron_right.svg";
import MediaQuery from "react-responsive";
import mediaQueries, { upFrom, upTo } from "../../styles/mediaQueries";

interface IProps {
  expiry?: number; // in minutes
  isMobile?: boolean; // Used to render different versions based on fix or not
  additionalCls?: string;
}

const RenderSeconds: React.FC<{ seconds: number; shouldHighlight: boolean }> = ({
  seconds,
  shouldHighlight,
}) => {
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  const paddedSec = String(sec).padStart(2, "0");
  // Render more verbosely on wider screens, use XX:YY-format on smaller
  return (
    <div
      className={`py-[1px] px-1 rounded-sm flex flex-row gap-x-0.5 items-center text-sm font-bold ${
        !shouldHighlight && "bg-reservationActive mx-0.5 rounded-md bg-opacity-30"
      }`}
    >
      <MediaQuery {...upFrom(mediaQueries.tablet)}>
        <span>{min} min</span>
        {min < 5 && (
          <span>
            <FormattedMessage
              id="components.ReservationExpiring.secs"
              defaultMessage={"and {sec} sec"}
              values={{
                sec,
              }}
            />
          </span>
        )}
      </MediaQuery>
      <MediaQuery {...upTo(mediaQueries.mobile)}>
        <span>
          {min}
          {min >= 5 ? " min" : ""}
        </span>
        {min < 5 && (
          <React.Fragment>
            <span>:</span>
            <span>{paddedSec}</span>
          </React.Fragment>
        )}
      </MediaQuery>
    </div>
  );
};

const OuterExpiry: React.FC<{
  children: React.ReactNode;
  shouldHighlight?: boolean;
  additionalCls: string;
}> = ({ children, shouldHighlight = false, additionalCls }) => {
  // This is the "pill"-shaped countdown, shouldHighlight is active when counter is below 5 mins. Add padding at this point as to not "move" the inner content
  // AdditionalCls only applies to this one, not Mobile
  return (
    <div
      className={`${
        shouldHighlight
          ? "bg-amber-200 border-[#EEDC67] border bg-opacity-90 py-1 px-3"
          : additionalCls
      } w-fit rounded-full flex justify-center flex-col relative`}
    >
      {children}
    </div>
  );
};

const MobileOuter: React.FC<{
  children: React.ReactNode;
  shouldHighlight?: boolean;
  hasContent?: boolean;
}> = ({ children, shouldHighlight, hasContent }) => {
  const [scrolledPast, setScrolledPast] = React.useState(false);
  const [elemHeight, setElemHeight] = React.useState(0);
  const headerRef = React.createRef<HTMLDivElement>();
  const outerRef = React.createRef<HTMLDivElement>();

  window.addEventListener("scroll", () => {
    // Top is the coordinate of the element relative to the top of the screen. When this is negative, user has scrolled past the element
    const top = outerRef.current?.getBoundingClientRect().top || 0;
    if (outerRef.current && headerRef.current && top < 0 && !scrolledPast) {
      setScrolledPast(true);
      setElemHeight(headerRef.current.offsetHeight);
    } else if (outerRef.current && top >= 0 && scrolledPast) {
      setScrolledPast(false);
    }
  });

  useEffect(() => {
    // Used to maintain height on header to prevent content from "jumping" around after changing to fixed
    if (headerRef.current) {
      setElemHeight(headerRef.current.offsetHeight);
    }
  }, [headerRef]);

  return (
    <div style={{ minHeight: elemHeight }} ref={outerRef}>
      <div
        ref={headerRef}
        style={
          scrolledPast && shouldHighlight
            ? // Used to give a slide-in animation for the element when user has scrolled past and the countdown passes below 5 min
              { top: -elemHeight, transform: `translateY(${elemHeight}px)` }
            : {}
        }
        className={
          scrolledPast && shouldHighlight ? "fixed w-full z-20 transition-transform" : "block"
        }
      >
        <div
          className={`${shouldHighlight ? "bg-amber-200" : "bg-white"} ${
            hasContent ? "py-2" : ""
          } w-full flex justify-center flex-col items-center relative`}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

const ReservationExpiring: React.FC<IProps> = ({
  expiry = 15,
  isMobile = false,
  additionalCls = "",
}) => {
  const [loading, setLoading] = React.useState(false);
  const { provided } = useReservationContext();
  const {
    reservationExpiry,
    setReservationExpiry,
    setReservationHasExpired,
    reservationHasExpired,
  } = provided;
  // Derived state, updated in effect and interval as a function of the countdown
  const [shouldHighlight, setShouldHighlight] = React.useState(false);

  const { pathname, state } = useLocation();
  const params = useParams();
  const { eventId } = params;
  // Used to give information about expired reservations when entering the page with an old reservation
  const expired = state?.expired;
  // Allow extending reservation on pages that might take a bit more time to complete
  const extendableReservation = !!pathname.match("(/information)|(/checkout/.*)");
  const navigate = useNavigate();
  // Dont highlight and show "Start over" when expired on base url
  const isBase = !!useMatch("/event/:eventId/?");

  const extendReservation = () => {
    setLoading(true);
    repinReservation({ eventId: eventId || "" }).then((res) => {
      if (res.status === "fetched") {
        setReservationExpiry(res.entity);
      }
      setLoading(false);
    });
  };

  const returnToBase = () => {
    // Cancel checkout first to avoid having to navigate the "You have an active reservation-modal"
    cancelCheckout(eventId || "").then(() => {
      navigate(paths.event(eventId || ""));
    });
  };

  const expiryTime = useMemo(
    () => new Date((reservationExpiry || new Date()).getTime() + expiry * 60000),
    [reservationExpiry, expiry],
  );

  const [secondsLeft, setSecondsLeft] = React.useState(differenceInSeconds(expiryTime, new Date()));

  React.useEffect(() => {
    // Main engine of the countdown, set initial countdown, then register an interval that updates every 500ms.
    // Used 1000ms at first, but if something causes the thread to pause for longer, you might end up with a double-count, which is avoided by updating more often.
    let interval: NodeJS.Timeout;
    const secsLeft = differenceInSeconds(expiryTime, new Date());
    if (secsLeft <= 0) {
      setShouldHighlight(!isBase);
      setReservationHasExpired(true);
      setSecondsLeft(secsLeft);
    } else if (reservationExpiry) {
      // Dont highlight on base
      setShouldHighlight(secsLeft < 300 && !isBase);
      setReservationHasExpired(false);
      setSecondsLeft(secsLeft);
      interval = setInterval(() => {
        const s = differenceInSeconds(expiryTime, new Date());
        setSecondsLeft(s);
        setShouldHighlight(s < 300 && !isBase);
        if (s <= 0) {
          setReservationHasExpired(true);
          clearInterval(interval);
        }
      }, 500);
    }
    // Effect cleanup
    return () => clearInterval(interval);
  }, [expiryTime, setReservationHasExpired, reservationExpiry, isBase]);

  const getInnerContent = () => {
    if (reservationExpiry !== null) {
      if (reservationHasExpired) {
        return (
          <div className="flex flex-row items-center text-sm">
            <div className="flex flex-row items-center gap-x-2">
              <IconTimeOutline
                height={18}
                width={18}
                className={`${
                  shouldHighlight ? "opacity-55" : "text-cornflowerBlue opacity-80"
                } hidden sm:block`}
              />
              <FormattedMessage
                id="components.ReservationExpiring.expired"
                defaultMessage={"Your reservation has expired"}
              />
            </div>
            {!isBase && (
              <button
                onClick={returnToBase}
                className={`sm:border-l items-center sm:border-[#EEDC67] hover:opacity-80 pl-2 sm:ml-3 flex flex-row gap-x-0.5 font-medium`}
              >
                <FormattedMessage
                  id="components.ReservationExpiring.goBackToStart"
                  defaultMessage={"Start over"}
                />
                <IconArrowRight width={16} className="opacity-40" />
              </button>
            )}
          </div>
        );
      } else {
        return (
          <div className="flex flex-row items-center text-sm">
            <div
              className={`flex flex-row items-center ${
                extendableReservation && secondsLeft < 120 ? "min-w-[220px] sm:min-w-[320px]" : ""
              }`}
            >
              <div className={`relative group`}>
                <div className="bg-white w-48 py-2 px-3 rounded-md shadow-sm border border-gray-100 group-hover:block hidden absolute top-5 left-0 text-xs z-30 text-black">
                  <FormattedMessage
                    tagName={"span"}
                    id="components.ReservationExpiring.information"
                    defaultMessage={
                      "Reservation is held for up to 15 minutes, and is renewed whenever proceed in a step."
                    }
                  />
                </div>
                <span className="text-sm flex flex-row gap-x-2 items-center">
                  <IconTimeOutline
                    height={18}
                    width={18}
                    className={`${
                      shouldHighlight ? "opacity-55" : "text-cornflowerBlue opacity-80"
                    } ${extendableReservation && shouldHighlight ? "hidden sm:block" : "block"}`}
                  />
                  <FormattedMessage
                    id="components.ReservationExpiring.expiresIn"
                    defaultMessage={"Reservation expires in"}
                  />
                </span>
              </div>
              <RenderSeconds seconds={secondsLeft} shouldHighlight={shouldHighlight} />
            </div>
            {extendableReservation && secondsLeft < 120 && (
              <button
                onClick={extendReservation}
                className="md:border-l items-center md:border-[#EEDC67] hover:opacity-80 pl-2 flex flex-row gap-x-0.5 font-medium"
                disabled={loading}
              >
                {loading ? (
                  <Spinner size="small" theme="white" />
                ) : (
                  <React.Fragment>
                    <FormattedMessage
                      id="components.ReservationExpiring.extend"
                      defaultMessage={"Extend"}
                    />
                    <IconArrowRight width={16} className="opacity-40" />
                  </React.Fragment>
                )}
              </button>
            )}
          </div>
        );
      }
    }
    if (expired) {
      // Case triggered by the location-state
      return (
        <div className="text-sm flex flex-row gap-x-2 items-center">
          <IconTimeOutline height={18} width={18} className={"text-cornflowerBlue opacity-80"} />
          <FormattedMessage
            tagName={"span"}
            id="components.ReservationExpiring.expired"
            defaultMessage={"Your reservation has expired."}
          />
        </div>
      );
    }
    return null;
  };

  return isMobile ? (
    <MobileOuter shouldHighlight={shouldHighlight} hasContent={reservationExpiry || expired}>
      {getInnerContent()}
    </MobileOuter>
  ) : (
    <OuterExpiry additionalCls={additionalCls} shouldHighlight={shouldHighlight}>
      {getInnerContent()}
    </OuterExpiry>
  );
};

export default ReservationExpiring;
